import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import Card from '../../common/Card/Card';
import Notification from '../../common/Notification/Notification';

import CountrySelect from '../../common/CountrySelect/CountrySelect';
import CategoryAutocomplete from '../../common/CategoryAutocomplete/CategoryAutocomplete';
import { UpgradeModal } from '../../subscriptions/UpgradeModal/UpgradeModal';
import * as resultsApi from '../../../api/results';
import { DiscoverSummary, BestSearch } from '../../../types/results.types';
import { useSearch } from '../../../hooks/useSearch';
import { useAuth } from '../../../hooks/useAuth';
import { CreateSearchParams, FilterInterpretation, GoogleMapsRequirements, SearchLimitError } from '../../../types/search.types';
import { interpretWebsiteFilter } from '../../../api/search';
import { geocodeCity, GeocodedCity } from '../../../api/cities';
import ConfirmSearchModal from '../ConfirmSearchModal/ConfirmSearchModal';
import { usePostHog } from 'posthog-js/react';
import { subscriptionsApi } from '../../../api/subscriptions';
import { balanceApi } from '../../../api/balance';
import { BalanceResponse } from '../../../types/balance.types';
import { Globe, Search, MapPin, Building2, CheckCircle2, SlidersHorizontal, X, Target, Users, Star, FileText, Sparkles, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAutoFindSuccessCap } from '../../../constants/autoFindCaps';
import { WEBSITE_MATCH_EXAMPLE } from '../../../constants/websiteMatchExample';
import RatingRangeField from './RatingRangeField';
import RefineOptionTile from './RefineOptionTile';
import {
  buildLeadRequirements,
  formatGoogleAdsSignal,
  formatRatingRange,
  GoogleAdsSignal,
  RATING_POSITION_MAX,
  RATING_POSITION_MIN,
  WebsiteStatus,
} from './leadFilters';

const SHOW_GOOGLE_MAPS_REQUIREMENTS = false;
const INVALID_TEXT_ERROR = "Allowed: letters, spaces, ' and -.";
const ALLOWED_TEXT_CHARACTERS = /^[\p{L}\p{M}\s''-]*$/u;
const STRICT_TEXT_PATTERN = /^\p{L}[\p{L}\p{M}]*(?:[ '\-'][\p{L}\p{M}]+)*$/u;
type SmartFilterUiState = 'idle' | 'generating' | 'approved' | 'dirty' | 'rejected' | 'failed';
type RefineEditor = 'website' | 'rating' | 'website-match' | 'google-ads' | null;

const normalizeForValidation = (value: string, trim = false): string => {
  const normalizedValue = value.normalize('NFC');
  return trim ? normalizedValue.trim() : normalizedValue;
};

const isStrictlyValidSearchText = (value: string): boolean => {
  return STRICT_TEXT_PATTERN.test(normalizeForValidation(value, true));
};

const hasOnlyAllowedCharacters = (value: string): boolean => {
  return ALLOWED_TEXT_CHARACTERS.test(normalizeForValidation(value));
};

const isSearchLimitError = (err: unknown): err is SearchLimitError =>
  typeof err === 'object' &&
  err !== null &&
  'message' in err &&
  typeof (err as SearchLimitError).message === 'string' &&
  'active_searches' in err &&
  typeof (err as SearchLimitError).active_searches === 'number' &&
  'max_allowed' in err &&
  typeof (err as SearchLimitError).max_allowed === 'number' &&
  'current_plan' in err &&
  typeof (err as SearchLimitError).current_plan === 'string';

const getQualifyingUpgradePlansCount = (
  plans: any[],
  currentPlan: string,
  maxAllowed: number
): number => {
  const normalizedCurrentPlan = (currentPlan || 'discover').toLowerCase();
  const isPaidPlan = normalizedCurrentPlan !== 'discover';

  return plans
    .filter((plan) => plan?.name && plan.name !== 'free' && plan.name !== normalizedCurrentPlan)
    .filter((plan) => (isPaidPlan ? plan.name !== 'discover' : true))
    .filter((plan) => (plan?.features?.max_queued_searches || 0) > maxAllowed)
    .length;
};

const resolveDefaultPaidPlanId = (plans: Array<{ id: number; name: string }>): number | undefined => {
  const paid = plans.filter((plan) => plan.name !== 'discover' && plan.name !== 'free');
  const growthPlan = paid.find((plan) => plan.name === 'growth');
  return growthPlan?.id ?? paid[0]?.id;
};

const hasSmartFiltersAccess = (planName: string): boolean => !['discover', 'free'].includes((planName || 'discover').toLowerCase());

const isFreePlan = (planName: string): boolean => ['discover', 'free'].includes((planName || 'discover').toLowerCase());

interface AutoFindPreviewProps {
  planName: string;
  balance: BalanceResponse | null;
  onUpgradeClick: () => void;
  noWebsite: boolean;
}

const AutoFindPreview: React.FC<AutoFindPreviewProps> = ({ planName, balance, onUpgradeClick, noWebsite }) => {
  if (noWebsite) {
    return (
      <section className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 sm:px-5">
        <div className="flex min-w-0 gap-3 sm:gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-gray-400 ring-1 ring-gray-200">
            <Target className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900 sm:text-base">Automatic email search is off</h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Email finding requires a business website. No lookups will be used.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const freePlan = isFreePlan(planName);
  const hasBalanceData = balance !== null;
  const successLimit = getAutoFindSuccessCap(planName);
  const remainingLookups = balance?.dm_unlocks_remaining ?? 0;
  const poolLimit = balance?.auto_find_monthly_pool_limit ?? 0;
  const poolRemaining = balance?.auto_find_monthly_pool_remaining ?? 0;

  let description: React.ReactNode;
  let statusContent: React.ReactNode;

  if (!hasBalanceData) {
    description = (
      <>
        <p className="mt-1 text-sm leading-relaxed text-gray-700">
          We&apos;ll try to find emails for up to {successLimit.toLocaleString()} decision makers in this search.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-gray-700">
          A lookup is used only when an email is found. <span className="font-semibold text-gray-900">No email found = no lookup used.</span>
        </p>
      </>
    );
    statusContent = (
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500">
        <Users className="h-4 w-4 shrink-0 text-purple-500" aria-hidden="true" />
        Loading lookups...
      </span>
    );
  } else if (freePlan) {
    if (remainingLookups <= 0) {
      description = (
        <>
          <p className="mt-1 text-sm leading-relaxed text-gray-700">
            We&apos;ll still find businesses and decision makers in this search, but automatic email finding will stay off until you have more lookups.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-gray-700">
            A lookup is used only when an email is found. <span className="font-semibold text-gray-900">No email found = no lookup used.</span>
          </p>
          <div className="mt-3">
            <Button onClick={onUpgradeClick} variant="primary" className="h-9 px-4 text-sm font-semibold">
              Upgrade
            </Button>
          </div>
        </>
      );
      statusContent = (
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500">
          <Users className="h-4 w-4 shrink-0 text-purple-500" aria-hidden="true" />
          0 lookups left
        </span>
      );
    } else if (remainingLookups < successLimit) {
      description = (
        <>
          <p className="mt-1 text-sm leading-relaxed text-gray-700">
            We&apos;ll try to find emails for up to {remainingLookups.toLocaleString()} decision makers in this search.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-gray-700">
            A lookup is used only when an email is found. <span className="font-semibold text-gray-900">No email found = no lookup used.</span>
          </p>
        </>
      );
      statusContent = (
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Users className="h-4 w-4 shrink-0 text-purple-500" aria-hidden="true" />
          <span>
            <span className="text-purple-600">{remainingLookups.toLocaleString()}</span> lookups left
          </span>
        </span>
      );
    } else {
      description = (
        <>
          <p className="mt-1 text-sm leading-relaxed text-gray-700">
            We&apos;ll try to find emails for up to {successLimit.toLocaleString()} decision makers in this search.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-gray-700">
            A lookup is used only when an email is found. <span className="font-semibold text-gray-900">No email found = no lookup used.</span>
          </p>
        </>
      );
      statusContent = (
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Users className="h-4 w-4 shrink-0 text-purple-500" aria-hidden="true" />
          <span>
            <span className="text-purple-600">{remainingLookups.toLocaleString()}</span> lookups left
          </span>
        </span>
      );
    }
  } else if (poolLimit > 0 && poolRemaining <= 0) {
    description = (
      <>
        <p className="mt-1 text-sm leading-relaxed text-gray-700">
          Your monthly automatic email finding credits are used up for now.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-gray-700">
          A lookup is used only when an email is found. You can still find emails manually below, and your credits reset on your billing date.
        </p>
      </>
    );
    statusContent = (
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500">
        <Users className="h-4 w-4 shrink-0 text-purple-500" aria-hidden="true" />
        0 lookups left
      </span>
    );
  } else {
    description = (
      <>
        <p className="mt-1 text-sm leading-relaxed text-gray-700">
          We&apos;ll try to find emails for up to {successLimit.toLocaleString()} decision makers in this search.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-gray-700">
          A lookup is used only when an email is found. <span className="font-semibold text-gray-900">No email found = no lookup used.</span>
        </p>
      </>
    );
    statusContent = (
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Users className="h-4 w-4 shrink-0 text-purple-500" aria-hidden="true" />
        <span>
          <span className="text-purple-600">{poolRemaining.toLocaleString()}</span> lookups left
        </span>
      </span>
    );
  }

  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-[#EEE6E1] bg-[#FFFBF7] shadow-sm">
      <div className="flex min-w-0 gap-3 p-4 sm:gap-5 sm:p-5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#8A3033] shadow-sm ring-1 ring-[#EEE6E1]">
          <Target className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-gray-900 sm:text-base">Automatic email search is on</h3>
          {description}
        </div>
      </div>
      <div className="border-t border-[#EEE6E1] px-4 py-3 sm:px-5">
        {statusContent}
      </div>
    </section>
  );
};

const SearchForm: React.FC = () => {
  const location = useLocation();
  const prefillQuery = (location.state as { search_query?: string } | null)?.search_query ?? '';
  const cameFromOnboarding = (location.state as { from?: string } | null)?.from === 'onboarding';
  const [showOnboardingNotice, setShowOnboardingNotice] = useState(cameFromOnboarding);

  const [searchParams, setSearchParams] = useState<CreateSearchParams>({
    search_query: prefillQuery,
    city_name: '',
    state_name: '',
    country: '',
    lat: 0,
    lng: 0,
  });
  
  const [geocodedCity, setGeocodedCity] = useState<GeocodedCity | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [successNotification, setSuccessNotification] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<string>('discover');
  const [currentPlanLoaded, setCurrentPlanLoaded] = useState(false);
  const [upgradeModalContext, setUpgradeModalContext] = useState<'generic' | 'search_limit' | 'discover_exhausted' | 'smart_filters'>('generic');
  const [searchLimitInfo, setSearchLimitInfo] = useState<{ activeSearches: number; maxAllowed: number } | undefined>(undefined);
  const [discoverSummary, setDiscoverSummary] = useState<DiscoverSummary | undefined>(undefined);
  const [discoverBestSearch, setDiscoverBestSearch] = useState<BestSearch | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [quotaPlans, setQuotaPlans] = useState<Array<{id:number;name:string;display_name:string;price_display:string;daily_searches:number;max_results:number|null|undefined;monthly_dm_unlocks:number|null|undefined;monthly_email_verifications:number|null|undefined;features:{max_queued_searches:number}}>>([]);
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);
  const [smartFilterEnabled, setSmartFilterEnabled] = useState(false);
  const [smartFilterDescription, setSmartFilterDescription] = useState('');
  const [smartFilterInterpretation, setSmartFilterInterpretation] = useState<FilterInterpretation | null>(null);
  const [smartFilterLoading, setSmartFilterLoading] = useState(false);
  const [smartFilterError, setSmartFilterError] = useState<string | null>(null);
  const [smartFilterState, setSmartFilterState] = useState<SmartFilterUiState>('idle');
  const [smartFilterNotice, setSmartFilterNotice] = useState<string | null>(null);
  const [smartFilterStale, setSmartFilterStale] = useState<{ previousBusinessType: string } | null>(null);
  const [smartFilterNewTags, setSmartFilterNewTags] = useState<Set<string>>(new Set());
  const smartFilterChipsRef = useRef<HTMLDivElement>(null);
  const [websiteStatus, setWebsiteStatus] = useState<WebsiteStatus>('any');
  const [ratingMinPosition, setRatingMinPosition] = useState(RATING_POSITION_MIN);
  const [ratingMaxPosition, setRatingMaxPosition] = useState(RATING_POSITION_MAX);
  const [googleAdsSignal, setGoogleAdsSignal] = useState<GoogleAdsSignal | undefined>(undefined);
  const [openRefineEditor, setOpenRefineEditor] = useState<RefineEditor>(null);

  const [googleMapsRequirements, setGoogleMapsRequirements] = useState({
    minReviewCount: '',
    maxReviewCount: '',
    minReviewRating: '',
    maxReviewRating: '',
    hasPhoneNumber: '',
    hasWebsite: '',
    temporaryClosed: '',
    lgbtqFriendly: '',
    latinoOwned: '',
    asianOwned: '',
    veteranOwned: '',
  });
  const { createSearch, loading, error, clearError } = useSearch();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const startPlanCheckout = async (planId: number, plan?: { name: string; display_name: string; price_display?: string }) => {
    setRedirectingToCheckout(true);
    posthog?.capture('upgrade_plan_selected', {
      context: 'quota_paywall',
      current_plan: currentPlan,
      selected_plan: plan?.name ?? null,
      searches_used_today: balance?.searches_used_today ?? null,
      total_places_found: balance?.total_places_found ?? null,
    });
    try {
      const successUrl = `${window.location.origin}/subscriptions?success=true`;
      const cancelUrl = `${window.location.origin}/subscriptions?canceled=true`;
      const response = await subscriptionsApi.createCheckoutSession(planId, successUrl, cancelUrl);
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }
    } catch (checkoutError) {
      console.error('Failed to create checkout session:', checkoutError);
    }
    setRedirectingToCheckout(false);
  };

  const startCheckoutWithDefaultPaidPlan = async () => {
    setRedirectingToCheckout(true);
    try {
      let defaultPlanId = resolveDefaultPaidPlanId(quotaPlans);
      if (defaultPlanId === undefined) {
        const plansResponse = await subscriptionsApi.getPlans();
        const paidPlans = (plansResponse.plans || []).filter(
          (plan: { name: string }) => plan.name !== 'discover' && plan.name !== 'free'
        );
        defaultPlanId = resolveDefaultPaidPlanId(paidPlans);
      }
      if (defaultPlanId === undefined) {
        setRedirectingToCheckout(false);
        navigate('/subscriptions?scrollTo=plans');
        return;
      }
      await startPlanCheckout(defaultPlanId);
    } catch (plansLoadError) {
      console.error('Failed to load plans for checkout:', plansLoadError);
      setRedirectingToCheckout(false);
      navigate('/subscriptions?scrollTo=plans');
    }
  };
  
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (!isAuthenticated) return;
      try {
        const subscription = await subscriptionsApi.getCurrentSubscription();
        setCurrentPlan(subscription.subscription?.plan_name || 'discover');
        if (subscription.balance) {
          setBalance(subscription.balance);
        }
      } catch (error) {
        console.error('Failed to fetch current subscription:', error);
      } finally {
        setCurrentPlanLoaded(true);
      }
    };
    
    fetchCurrentPlan();
  }, [isAuthenticated]);

  useEffect(() => {
    const loadBalance = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await balanceApi.getBalance();
        setBalance(data);
      } catch (error) {
        console.error('Failed to load balance:', error);
      }
    };
    loadBalance();
    const interval = setInterval(loadBalance, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  
  const isOutOfQuota = balance !== null && balance.searches_remaining === 0;

  useEffect(() => {
    if (!isOutOfQuota || quotaPlans.length > 0) return;
    subscriptionsApi.getPlans().then((res) => {
      const paid = (res.plans || []).filter((p: any) => p.name !== 'discover' && p.name !== 'free');
      setQuotaPlans(paid);
    }).catch(() => {});
  }, [isOutOfQuota, quotaPlans.length]);

  useEffect(() => {
    if (!isOutOfQuota || discoverSummary) return;
    Promise.all([resultsApi.getUserSummary(), resultsApi.getBestSearch()])
      .then(([summary, bestSearch]) => {
        setDiscoverSummary(summary);
        setDiscoverBestSearch(bestSearch);
      })
      .catch(() => {});
  }, [isOutOfQuota, discoverSummary]);

  useEffect(() => {
    if (!isOutOfQuota) return;
    posthog?.capture('quota_paywall_viewed', {
      current_plan: currentPlan,
      searches_used_today: balance?.searches_used_today ?? null,
      total_places_found: balance?.total_places_found ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOutOfQuota]);

  useEffect(() => {
    let cancelled = false;

    const maybeOpenUpgradeModal = async () => {
      if (!error) return;

      let errorData: any = null;
      
      if (typeof error === 'object' && 'detail' in error) {
        errorData = (error as any).detail;
      } else if (typeof error === 'object') {
        errorData = error;
      }
      
      if (!errorData || errorData.suggested_action !== 'upgrade_plan') return;

      if (errorData.limit_type === 'lifetime_exhausted') {
        try {
          const [summary, bestSearch] = await Promise.all([
            resultsApi.getUserSummary(),
            resultsApi.getBestSearch(),
          ]);
          if (cancelled) return;
          setDiscoverSummary(summary);
          setDiscoverBestSearch(bestSearch);
        } catch (summaryError) {
          console.error('Failed to load discover summary:', summaryError);
          setDiscoverSummary(undefined);
        }
        setUpgradeModalContext('discover_exhausted');
        setSearchLimitInfo(undefined);
      } else if (isSearchLimitError(errorData) && errorData.limit_type === 'active_searches') {
        try {
          const plansResponse = await subscriptionsApi.getPlans();
          if (cancelled) return;

          const plans = Array.isArray(plansResponse?.plans) ? plansResponse.plans : [];
          const qualifyingPlansCount = getQualifyingUpgradePlansCount(
            plans,
            errorData.current_plan,
            errorData.max_allowed
          );

          // If there is no valid higher plan, keep inline guidance only.
          if (qualifyingPlansCount === 0) {
            return;
          }
        } catch (plansError) {
          console.error('Failed to pre-check qualifying upgrade plans:', plansError);
          // Do not show modal if we cannot confirm a valid upgrade path.
          return;
        }

        setUpgradeModalContext('search_limit');
        setSearchLimitInfo({
          activeSearches: errorData.active_searches,
          maxAllowed: errorData.max_allowed,
        });
      } else if (errorData.feature === 'smart_filters') {
        if (typeof errorData.current_plan === 'string') {
          setCurrentPlan(errorData.current_plan);
        }
        setUpgradeModalContext('smart_filters');
        setSearchLimitInfo(undefined);
      } else {
        setUpgradeModalContext('generic');
        setSearchLimitInfo(undefined);
      }

      if (cancelled) return;
      setShowUpgradeModal(true);
      clearError();
    };

    void maybeOpenUpgradeModal();

    return () => {
      cancelled = true;
    };
  }, [error, clearError]);

  const clearFieldError = (fieldName: string) => {
    setFieldErrors(prev => {
      if (!prev[fieldName]) {
        return prev;
      }
      const nextErrors = { ...prev };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const clearSmartFilterFeedback = () => {
    setSmartFilterError(null);
    setSmartFilterNotice(null);
  };

  const openSmartFiltersUpgradeModal = (source = 'search_create_refine') => {
    setUpgradeModalContext('smart_filters');
    setSearchLimitInfo(undefined);
    setDiscoverSummary(undefined);
    setShowUpgradeModal(true);
    posthog?.capture('smart_filters_paywall_opened', {
      current_plan: currentPlan,
      business_type: searchParams.search_query.trim() || null,
      source,
    });
  };

  const resetSmartFilterInterpretation = (nextState: SmartFilterUiState = 'idle') => {
    setSmartFilterInterpretation(null);
    setSmartFilterState(nextState);
  };

  const clearSmartFilterSection = (options?: { notice?: string; keepEnabled?: boolean }) => {
    setSmartFilterEnabled(options?.keepEnabled ?? false);
    setSmartFilterDescription('');
    setSmartFilterLoading(false);
    setSmartFilterError(null);
    setSmartFilterInterpretation(null);
    setSmartFilterState('idle');
    setSmartFilterNotice(options?.notice ?? null);
    setSmartFilterStale(null);
  };

  const handleWebsiteStatusChange = (nextStatus: WebsiteStatus) => {
    if (smartFilterEnabled && nextStatus !== 'has') {
      clearSmartFilterSection({
        notice: nextStatus === 'none'
          ? 'Website Match was turned off because it requires business websites.'
          : 'Website Match was turned off because it only applies to businesses with websites.',
      });
      posthog?.capture('website_filter_disabled_by_website_status', {
        website_status: nextStatus,
      });
    }
    if (nextStatus !== 'has' && googleAdsSignal !== undefined) {
      setGoogleAdsSignal(undefined);
      posthog?.capture('google_ads_signal_disabled_by_website_status', {
        website_status: nextStatus,
      });
    }
    setWebsiteStatus(nextStatus);
    if (nextStatus === 'none' && openRefineEditor === 'website-match') {
      setOpenRefineEditor(null);
    }
    if (nextStatus === 'none' && openRefineEditor === 'google-ads') {
      setOpenRefineEditor(null);
    }
  };

  const handleGoogleAdsSignalChange = (value: GoogleAdsSignal | undefined) => {
    setGoogleAdsSignal(value);
    if (value !== undefined && websiteStatus !== 'has') {
      setWebsiteStatus('has');
    }
    posthog?.capture('google_ads_signal_priority_selected', {
      value: value ?? 'any',
      source: 'search_form',
    });
  };

  const hasApprovedSmartFilter =
    smartFilterEnabled &&
    smartFilterState === 'approved' &&
    smartFilterInterpretation !== null &&
    smartFilterInterpretation.status !== 'rejected' &&
    smartFilterInterpretation.offerings.length > 0;
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (showOnboardingNotice) setShowOnboardingNotice(false);

    if (name === 'city_name' || name === 'state_name') {
      if (!hasOnlyAllowedCharacters(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [name]: INVALID_TEXT_ERROR
        }));
      } else {
        clearFieldError(name);
      }

      setSearchParams(prev => ({
        ...prev,
        [name]: value
      }));
      return;
    }
    
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryChange = (category: string) => {
    if (showOnboardingNotice) setShowOnboardingNotice(false);

    const trimmedCategory = category.trim();
    const businessTypeChanged = searchParams.search_query.trim() !== trimmedCategory;

    setSearchParams(prev => ({ ...prev, search_query: category }));
    if (!hasOnlyAllowedCharacters(category)) {
      setFieldErrors(prev => ({
        ...prev,
        search_query: INVALID_TEXT_ERROR
      }));
    } else {
      clearFieldError('search_query');
    }

    if (businessTypeChanged && (smartFilterEnabled || smartFilterDescription.trim() || smartFilterInterpretation)) {
      if (!trimmedCategory) {
        clearSmartFilterSection({
          notice: 'Website match was cleared. Choose a business type to use it again.',
        });
        setSmartFilterStale(null);
      } else if (smartFilterDescription.trim() || smartFilterInterpretation) {
        setSmartFilterStale({ previousBusinessType: searchParams.search_query.trim() });
        setSmartFilterNotice(null);
        setSmartFilterError(null);
      } else {
        clearSmartFilterSection();
        setSmartFilterStale(null);
      }
      posthog?.capture('website_filter_business_type_reset');
    }
  };

  const handleInterpretFilter = async () => {
    clearSmartFilterFeedback();
    setSmartFilterLoading(true);
    setSmartFilterState('generating');
    setSmartFilterNewTags(new Set());
    posthog?.capture('website_filter_generation_started');
    setSmartFilterError(null);
    const previousOfferings = smartFilterInterpretation?.offerings ?? [];
    try {
      const result = await interpretWebsiteFilter(searchParams.search_query, smartFilterDescription);
      setSmartFilterInterpretation(result);
      posthog?.capture('website_filter_interpreted', {
        status: result.status,
        offering_count: result.offerings.length,
      });
      if (result.status === 'rejected') {
        setSmartFilterState('rejected');
        setSmartFilterError(result.reason || 'These filters conflict. Please rewrite them.');
        posthog?.capture('website_filter_rejected', {
          has_suggested_rewrite: Boolean(result.suggested_rewrite),
        });
      } else {
        setSmartFilterState('approved');
        const prevSet = new Set(previousOfferings);
        const newTags = new Set(result.offerings.filter(t => !prevSet.has(t)));
        setSmartFilterNewTags(newTags);
        if (newTags.size > 0) {
          setTimeout(() => {
            smartFilterChipsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setTimeout(() => setSmartFilterNewTags(new Set()), 2500);
          }, 50);
        }
      }
    } catch (error: any) {
      const errorData = error?.response?.data?.detail;
      if (error?.response?.status === 402 && errorData?.feature === 'smart_filters') {
        if (typeof errorData.current_plan === 'string') {
          setCurrentPlan(errorData.current_plan);
        }
        setSmartFilterState('idle');
        setSmartFilterError(null);
        openSmartFiltersUpgradeModal();
        return;
      }
      setSmartFilterState('failed');
      setSmartFilterError('We could not interpret this filter. You can retry or continue without it.');
      posthog?.capture('website_filter_interpretation_failed');
    } finally {
      setSmartFilterLoading(false);
    }
  };

  const removeSmartFilterTag = (tag: string) => {
    posthog?.capture('website_filter_tag_removed', { group: 'offerings', tag });
    setSmartFilterInterpretation(current => current ? {
      ...current,
      offerings: current.offerings.filter(item => item !== tag),
    } : current);
    clearSmartFilterFeedback();
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (showOnboardingNotice) setShowOnboardingNotice(false);

    if (!isAuthenticated) {
      console.log('SearchForm: Not authenticated, cannot create search');
      return;
    }
    if (isOutOfQuota) {
      setUpgradeModalContext('discover_exhausted');
      setShowUpgradeModal(true);
      posthog?.capture('quota_paywall_modal_opened', {
        current_plan: currentPlan,
        searches_used_today: balance?.searches_used_today ?? null,
        total_places_found: balance?.total_places_found ?? null,
      });
      return;
    }
    if (smartFilterEnabled && smartFilterStale) {
      setSmartFilterError('Regenerate your website match filters for the new business type, or choose to keep the existing ones.');
      return;
    }
    if (
      smartFilterEnabled &&
      (
        !smartFilterInterpretation ||
        smartFilterState !== 'approved' ||
        smartFilterInterpretation.status === 'rejected' ||
        smartFilterInterpretation.offerings.length === 0
      )
    ) {
      setSmartFilterError(
        smartFilterState === 'dirty'
          ? 'Your service request changed. Generate updated website match filters or turn off website match.'
          : 'Review and approve at least one service tag before continuing, or turn off website match.'
      );
      return;
    }
    
    const normalizedSearchQuery = normalizeForValidation(searchParams.search_query, true);
    const normalizedCityName = normalizeForValidation(searchParams.city_name, true);
    const normalizedStateName = normalizeForValidation(searchParams.state_name, true);

    const nextFieldErrors: Record<string, string> = {};

    if (!normalizedSearchQuery) {
      nextFieldErrors.search_query = 'Business type is required';
    } else if (!isStrictlyValidSearchText(normalizedSearchQuery)) {
      nextFieldErrors.search_query = INVALID_TEXT_ERROR;
    }

    if (!normalizedCityName) {
      nextFieldErrors.city_name = 'City is required';
    } else if (!isStrictlyValidSearchText(normalizedCityName)) {
      nextFieldErrors.city_name = INVALID_TEXT_ERROR;
    }

    if (!normalizedStateName) {
      nextFieldErrors.state_name = 'State/Province is required';
    } else if (!isStrictlyValidSearchText(normalizedStateName)) {
      nextFieldErrors.state_name = INVALID_TEXT_ERROR;
    }

    if (!searchParams.country) {
      nextFieldErrors.country = 'Country is required';
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(prev => {
        const nextErrors = { ...prev };
        delete nextErrors.search_query;
        delete nextErrors.city_name;
        delete nextErrors.state_name;
        delete nextErrors.country;
        return { ...nextErrors, ...nextFieldErrors };
      });
      const firstErrorField = Object.keys(nextFieldErrors)[0];
      const el = document.getElementById(firstErrorField);
      if (el) el.focus();
      return;
    }

    clearFieldError('search_query');
    clearFieldError('city_name');
    clearFieldError('state_name');
    clearFieldError('country');

    setGeocodingLoading(true);
    setGeocodingError(null);
    
    try {
      // Get geocoding data first
      const city = await geocodeCity(
        searchParams.city_name,
        searchParams.state_name,
        searchParams.country
      );
      
      setGeocodedCity(city);
      
      // Update search params with geocoded coordinates
      setSearchParams(prev => ({
        ...prev,
        lat: city.lat,
        lng: city.lng
      }));
      
      // Show confirmation modal
      setShowConfirmModal(true);
    } catch (err: any) {
      console.error('SearchForm: Error geocoding', err);
      // Ensure error is a string, not an object
      const errorMessage = typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Location not found. Double-check the city name, state/province, and country, then try again.';
      setGeocodingError(errorMessage);
    } finally {
      setGeocodingLoading(false);
    }
  };
  
  const buildGoogleMapsRequirements = (): GoogleMapsRequirements | undefined => {
    const r = googleMapsRequirements;
    const out: GoogleMapsRequirements = {};
    const minRc = r.minReviewCount.trim() ? parseInt(r.minReviewCount, 10) : null;
    if (minRc != null && !Number.isNaN(minRc)) out.min_review_count = minRc;
    const maxRc = r.maxReviewCount.trim() ? parseInt(r.maxReviewCount, 10) : null;
    if (maxRc != null && !Number.isNaN(maxRc)) out.max_review_count = maxRc;
    const minRr = r.minReviewRating.trim() ? parseFloat(r.minReviewRating) : null;
    if (minRr != null && !Number.isNaN(minRr)) out.min_review_rating = minRr;
    const maxRr = r.maxReviewRating.trim() ? parseFloat(r.maxReviewRating) : null;
    if (maxRr != null && !Number.isNaN(maxRr)) out.max_review_rating = maxRr;
    if (r.hasPhoneNumber === 'true') out.has_phone_number = true;
    if (r.hasPhoneNumber === 'false') out.has_phone_number = false;
    if (r.hasWebsite === 'true') out.has_website = true;
    if (r.hasWebsite === 'false') out.has_website = false;
    if (r.temporaryClosed === 'true') out.temporary_closed = true;
    if (r.temporaryClosed === 'false') out.temporary_closed = false;
    if (r.lgbtqFriendly === 'true') out.lgbtq_friendly = true;
    if (r.lgbtqFriendly === 'false') out.lgbtq_friendly = false;
    if (r.latinoOwned === 'true') out.latino_owned = true;
    if (r.latinoOwned === 'false') out.latino_owned = false;
    if (r.asianOwned === 'true') out.asian_owned = true;
    if (r.asianOwned === 'false') out.asian_owned = false;
    if (r.veteranOwned === 'true') out.veteran_owned = true;
    if (r.veteranOwned === 'false') out.veteran_owned = false;
    if (Object.keys(out).length === 0) return undefined;
    return out;
  };

  const buildCurrentLeadRequirements = (): GoogleMapsRequirements | undefined => {
    const leadRequirements = buildLeadRequirements(
      websiteStatus,
      ratingMinPosition,
      ratingMaxPosition,
    );
    if (!SHOW_GOOGLE_MAPS_REQUIREMENTS) return leadRequirements;

    const advancedRequirements = buildGoogleMapsRequirements();
    const merged = { ...advancedRequirements, ...leadRequirements };
    return Object.keys(merged).length > 0 ? merged : undefined;
  };

  const handleConfirmSearch = async (radius?: number) => {
    if (!geocodedCity) return;
    setShowConfirmModal(false);

    const searchData: CreateSearchParams = {
      ...searchParams,
      radius: radius,
      basic_business_info: true,
      business_categories: true,
      websites: true,
      business_emails: true,
      social_media_links: true,
      decision_makers: true,
      email_enrichment: true,
      category_input: {
        is_custom: true,
        resolved_categories: [searchParams.search_query],
        resolution_status: 'user_confirmed',
        resolution_confidence: 1,
      },
    };
    if (hasApprovedSmartFilter && smartFilterInterpretation) {
      searchData.website_filter = {
        enabled: true,
        description: smartFilterDescription,
        interpretation: smartFilterInterpretation.interpretation,
        offerings: smartFilterInterpretation.offerings,
        short_offerings: smartFilterInterpretation.short_offerings,
        customers: [],
        warnings: smartFilterInterpretation.warnings,
      };
    }
    const requirements = buildCurrentLeadRequirements();
    if (requirements) searchData.google_maps_requirements = requirements;
    if (googleAdsSignal) searchData.google_ads_signal = googleAdsSignal;

    try {
      const createdSearch = await createSearch(searchData);
      if (createdSearch && createdSearch.id) {
        navigate(`/searches/${createdSearch.id}?fromCreate=true`);
      }
    } catch (err) {
      console.error('SearchForm: Error creating search', err);
    }
  };
  
  // Show a message if not authenticated
  if (!isAuthenticated) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <span className="text-gray-600">Please log in to create a new search</span>
        </div>
      </Card>
    );
  }
  
  const stringErrorMessage = error && typeof error === 'string' ? error : null;
  const limitError = error && isSearchLimitError(error) ? error : null;
  const geocodingErrorMessage = geocodingError ? (typeof geocodingError === 'string' ? geocodingError : JSON.stringify(geocodingError)) : null;
  const smartFilterActionDisabled =
    smartFilterLoading || smartFilterDescription.trim().length < 3 || !searchParams.search_query.trim();
  const smartFilterActionHint = !searchParams.search_query.trim()
    ? 'Choose a business type first.'
    : smartFilterDescription.trim().length < 3
      ? ''
      : null;
  const websiteFilterApplied = websiteStatus !== 'any';
  const ratingFilterApplied = ratingMinPosition !== RATING_POSITION_MIN || ratingMaxPosition !== RATING_POSITION_MAX;
  const websiteMatchUnavailable = websiteStatus === 'none';
  const canUseWebsiteMatch = currentPlanLoaded && hasSmartFiltersAccess(currentPlan);
  const websiteMatchAppliedLabel = hasApprovedSmartFilter && smartFilterInterpretation
    ? `${smartFilterInterpretation.offerings.length} service${smartFilterInterpretation.offerings.length === 1 ? '' : 's'}`
    : smartFilterEnabled
      ? 'Being configured'
      : undefined;
  const googleAdsUnavailable = websiteStatus === 'none';
  const googleAdsFilterApplied = googleAdsSignal !== undefined;
  const googleAdsAppliedLabel = formatGoogleAdsSignal(googleAdsSignal);

  const toggleRefineEditor = (editor: Exclude<RefineEditor, null>) => {
    if (editor === 'website-match' && websiteMatchUnavailable) return;
    if (editor === 'google-ads' && googleAdsUnavailable) return;

    const nextEditor = openRefineEditor === editor ? null : editor;
    setOpenRefineEditor(nextEditor);

    if (editor === 'website-match' && nextEditor && canUseWebsiteMatch && !smartFilterEnabled) {
      setSmartFilterEnabled(true);
      setWebsiteStatus('has');
      clearSmartFilterFeedback();
      setSmartFilterState(smartFilterDescription.trim() ? 'dirty' : 'idle');
      posthog?.capture('website_filter_enabled', { source: 'refine_tile' });
    }
  };

  return (
    <>
      <Card className="max-w-5xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 md:text-2xl">Find local businesses to contact</h2>
          <p className="mt-1 text-sm text-gray-600 md:text-base">Tell us what you are looking for and where to search.</p>
        </div>

        {showOnboardingNotice && (
          <div className="mb-4 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-secondary">
            Pre-filled from your setup, change anything you like.
          </div>
        )}

        {limitError && (
          <div className="rounded-md p-3 mb-4 flex items-start border bg-red-50 border-red-400">
            <div className="flex-1 text-red-800">
              <p className="text-sm font-medium">{limitError.message}</p>
              <p className="text-sm mt-1">
                {limitError.active_searches} of {limitError.max_allowed} active search{limitError.max_allowed !== 1 ? 'es' : ''}. Current plan: {limitError.current_plan}.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {limitError.suggested_action === 'upgrade_plan' && !(showUpgradeModal && upgradeModalContext === 'search_limit') && (
                  <button
                    type="button"
                    disabled={redirectingToCheckout}
                    className="text-sm font-medium text-primary hover:text-primary-hover underline disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      void startCheckoutWithDefaultPaidPlan();
                    }}
                  >
                    Upgrade plan
                  </button>
                )}
                <Link
                  to="/searches"
                  className="text-sm font-medium text-red-700 hover:text-red-800 underline"
                >
                  View active searches
                </Link>
              </div>
            </div>
            <button
              type="button"
              className="ml-auto pl-3 inline-flex rounded p-1.5 focus:outline-none focus:ring-2 text-red-800"
              onClick={clearError}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {stringErrorMessage && (
          <Notification
            type="error"
            message={stringErrorMessage}
            onClose={clearError}
          />
        )}
        
        {geocodingErrorMessage && (
          <Notification
            type="error"
            message={geocodingErrorMessage}
            onClose={() => setGeocodingError(null)}
          />
        )}
        
        {successNotification && (
          <Notification
            type="success"
            message={successNotification}
            onClose={() => setSuccessNotification(null)}
          />
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="order-1 mb-4">
            <div className="mb-3 flex items-center">
              <Search className="mr-2 h-5 w-5 shrink-0 text-primary md:h-6 md:w-6" />
              <label htmlFor="search_query" className="block text-base font-semibold text-gray-700">
                Business Type <span className="text-primary">*</span>
              </label>
            </div>
            <CategoryAutocomplete
              id="search_query"
              value={searchParams.search_query}
              onChange={handleCategoryChange}
              placeholder="e.g., Dentist"
              error={fieldErrors.search_query}
            />
            <p className="mt-2 text-sm text-gray-500">
              Start with the kind of business you want to find.
            </p>
          </div>

          <section className="order-3 mb-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#8A3033] ring-1 ring-gray-200">
                <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Refine your list</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                  Optional. Use these preferences to narrow your results.
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <RefineOptionTile
                title="Website availability"
                description="Find businesses with or without a website."
                icon={<Globe className="h-4 w-4" aria-hidden="true" />}
                expanded={openRefineEditor === 'website'}
                appliedLabel={websiteFilterApplied ? (websiteStatus === 'has' ? 'Has website' : 'No website') : undefined}
                appliedHint={websiteStatus === 'has' && smartFilterEnabled && googleAdsFilterApplied
                  ? 'Required by Website Match and Google Ads'
                  : websiteStatus === 'has' && smartFilterEnabled
                    ? 'Required by Website Match'
                    : websiteStatus === 'has' && googleAdsFilterApplied
                      ? 'Required by Google Ads'
                      : undefined}
                onClick={() => toggleRefineEditor('website')}
                controls="website-availability-editor"
              />
              <RefineOptionTile
                title="Google rating"
                description="Target a specific rating range."
                icon={<Star className="h-4 w-4" aria-hidden="true" />}
                expanded={openRefineEditor === 'rating'}
                appliedLabel={ratingFilterApplied ? formatRatingRange(ratingMinPosition, ratingMaxPosition) : undefined}
                onClick={() => toggleRefineEditor('rating')}
                controls="rating-editor"
              />
              <RefineOptionTile
                title="Website Match"
                description={websiteMatchUnavailable
                  ? 'Website Match needs a business website to inspect.'
                  : 'e.g.: HVAC contractors who offer emergency service'}
                icon={<FileText className="h-4 w-4" aria-hidden="true" />}
                expanded={openRefineEditor === 'website-match'}
                appliedLabel={websiteMatchAppliedLabel}
                disabled={websiteMatchUnavailable}
                onClick={() => toggleRefineEditor('website-match')}
                controls="website-match-editor"
              />
              <RefineOptionTile
                title="Google Ads"
                description={googleAdsUnavailable
                  ? 'Needs a business website to detect ads.'
                  : <><span className="font-bold">Prioritize</span> businesses by whether they use Google Ads on their website.</>}
                icon={<Megaphone className="h-4 w-4" aria-hidden="true" />}
                expanded={openRefineEditor === 'google-ads'}
                appliedLabel={googleAdsAppliedLabel}
                disabled={googleAdsUnavailable}
                onClick={() => toggleRefineEditor('google-ads')}
                controls="google-ads-editor"
              />
            </div>

            {openRefineEditor === 'website' && (
              <fieldset id="website-availability-editor" className="mt-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <legend className="sr-only">Website availability</legend>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Website availability</h4>
                    <p className="mt-1 text-xs text-gray-500">Choose which businesses should be included.</p>
                  </div>
                  {websiteFilterApplied && (
                    <button type="button" onClick={() => handleWebsiteStatusChange('any')} className="text-sm font-semibold text-[#8A3033] underline underline-offset-2">
                      Reset
                    </button>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {([
                    ['any', 'Any'],
                    ['has', 'Has a website'],
                    ['none', 'No website'],
                  ] as Array<[WebsiteStatus, string]>).map(([value, label]) => {
                    const selected = websiteStatus === value;
                    return (
                      <label key={value} className={`flex min-h-[46px] cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[#8A3033] ${selected ? 'border-[#8A3033] bg-[#8A3033]/10 text-[#8A3033]' : 'border-gray-300 bg-white text-gray-700 hover:border-[#8A3033]/50'}`}>
                        <input type="radio" name="website-availability" value={value} checked={selected} onChange={() => handleWebsiteStatusChange(value)} className="sr-only" />
                        {selected && <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                        {label}
                      </label>
                    );
                  })}
                </div>
                {websiteStatus === 'none' && (
                  <p className="mt-3 text-xs leading-relaxed text-amber-700">
                    Businesses without websites won&apos;t have decision maker emails.
                  </p>
                )}
              </fieldset>
            )}

            {openRefineEditor === 'rating' && (
              <div id="rating-editor" className="mt-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <RatingRangeField
                  minPosition={ratingMinPosition}
                  maxPosition={ratingMaxPosition}
                  onChange={(minimum, maximum) => {
                    setRatingMinPosition(minimum);
                    setRatingMaxPosition(maximum);
                  }}
                />
              </div>
            )}

            {openRefineEditor === 'google-ads' && (
              <fieldset id="google-ads-editor" className="mt-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <legend className="sr-only">Google Ads</legend>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Google Ads</h4>
                    <p className="mt-1 text-xs text-gray-500">Businesses matching your preference are <b>prioritized</b>, so you&apos;ll see more of them in your results.</p>
                  </div>
                  {googleAdsFilterApplied && (
                    <button type="button" onClick={() => handleGoogleAdsSignalChange(undefined)} className="text-sm font-semibold text-[#8A3033] underline underline-offset-2">
                      Reset
                    </button>
                  )}
                </div>
                <div role="radiogroup" aria-label="Google Ads" className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {([
                    [undefined, 'No preference'],
                    ['detected_recently', 'Uses Google Ads'],
                    ['no_ad_tracking', 'No Google Ads'],
                  ] as Array<[GoogleAdsSignal | undefined, string]>).map(([value, label]) => {
                    const selected = googleAdsSignal === value;
                    return (
                      <button
                        key={label}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => handleGoogleAdsSignalChange(value)}
                        className={`flex min-h-[46px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033] ${selected ? 'border-[#8A3033] bg-[#8A3033]/10 text-[#8A3033]' : 'border-gray-300 bg-white text-gray-700 hover:border-[#8A3033]/50'}`}
                      >
                        {selected && <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs leading-relaxed text-gray-500">
                  Detected from the business website. Requires a website; campaigns may not be currently active.
                </p>
              </fieldset>
            )}

            {openRefineEditor === 'website-match' && currentPlanLoaded && !canUseWebsiteMatch && (
              <div id="website-match-editor" className="mt-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <div className="flex items-center gap-2 text-[#8A3033]">
                  <FileText className="h-5 w-5" aria-hidden="true" />
                  <span className="text-xs font-bold uppercase tracking-wide">Website Match</span>
                </div>
                <h4 className="mt-3 text-base font-semibold text-gray-900">Match businesses by what their website says</h4>
                <p className="mt-1.5 text-sm text-gray-600">
                  Choose the services you care about. We&apos;ll prioritize websites that mention them.
                </p>
                <div className="mt-4 rounded-xl border border-[#EEE6E1] bg-[#FFFBF7] p-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A3033]">Example</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{WEBSITE_MATCH_EXAMPLE.businessType}</p>
                  <div className="mt-3 flex flex-wrap gap-2" aria-label="Example Website Match keywords">
                    {WEBSITE_MATCH_EXAMPLE.filters.map((keyword) => (
                      <span key={keyword} className="rounded-full bg-[#F5ECE8] px-3 py-1 text-xs font-medium text-[#5C3D2E]">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button type="button" onClick={() => openSmartFiltersUpgradeModal('search_create_explainer')} className="min-h-[44px]">
                    Upgrade to use Website Match
                  </Button>
                  <button type="button" onClick={() => setOpenRefineEditor(null)} className="min-h-[44px] px-4 text-sm font-semibold text-gray-600 hover:text-gray-900">
                    Not now
                  </button>
                </div>
                <p className="mt-3 text-xs text-gray-400">Website Match is available on paid plans.</p>
              </div>
            )}
          </section>

          {openRefineEditor === 'website-match' && canUseWebsiteMatch && (
          <section id="website-match-editor" className="order-4 mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="hidden">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#8A3033] ring-1 ring-gray-200">
                  <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-900">Lead filters</h3>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500 ring-1 ring-gray-200">Optional</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">
                    Narrow results by website availability and Google rating. Skip this section for the widest reach.
                  </p>
                </div>
              </div>

              <fieldset className="mt-5">
                <legend className="text-sm font-semibold text-gray-900">Website status</legend>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {([
                    ['any', 'Any'],
                    ['has', 'Has a website'],
                    ['none', 'No website'],
                  ] as Array<[WebsiteStatus, string]>).map(([value, label]) => {
                    const selected = websiteStatus === value;
                    return (
                      <label
                        key={value}
                        className={`flex min-h-[48px] cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[#8A3033] ${
                          selected
                            ? 'border-[#8A3033] bg-[#8A3033]/10 text-[#8A3033]'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-[#8A3033]/50 hover:bg-[#FDF9F9]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="website-status"
                          value={value}
                          checked={selected}
                          onChange={() => handleWebsiteStatusChange(value)}
                          className="sr-only"
                        />
                        {selected && <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                        {label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>

            <div className="hidden">
              <RatingRangeField
                minPosition={ratingMinPosition}
                maxPosition={ratingMaxPosition}
                onChange={(minimum, maximum) => {
                  setRatingMinPosition(minimum);
                  setRatingMaxPosition(maximum);
                }}
              />
            </div>

          {(() => {
            const hasBusinessType = !!searchParams.search_query.trim();
            const sectionDisabled = !hasBusinessType;
            const websiteIncompatible = websiteStatus === 'none';
            const toggleDisabled = websiteIncompatible;
            return (
              <div className={`border-t border-gray-200 transition-colors ${
                toggleDisabled
                  ? 'bg-gray-50/70'
                  : smartFilterEnabled
                    ? 'bg-[#FDF9F9]'
                    : 'bg-white/70'
              }`}>
                <div className="hidden">
                  <span className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg sm:flex ${
                    smartFilterEnabled && !toggleDisabled ? 'bg-[#8A3033]/10 text-[#8A3033]' : 'bg-white text-gray-500'
                  }`}>
                    <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-gray-900">Website Match</h3>
                      {!websiteIncompatible && currentPlanLoaded && !hasSmartFiltersAccess(currentPlan) && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-[#8A3033]/25 bg-[#8A3033]/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#8A3033]"
                          title="Included with Starter and higher plans"
                        >
                          <Sparkles className="h-3 w-3" aria-hidden="true" />
                        
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600">
                      {websiteIncompatible
                        ? 'Website Match is unavailable when searching for businesses without websites.'
                        : sectionDisabled
                        ? 'Enter a business type above, then refine by website keywords.'
                        : 'Filter businesses whose site mentions specific words, e.g. "emergency service", "home-service", "insurance claims".'}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={smartFilterEnabled}
                    disabled={toggleDisabled}
                    onClick={() => {
                      if (toggleDisabled) return;
                      if (!smartFilterEnabled && currentPlanLoaded && !hasSmartFiltersAccess(currentPlan)) {
                        openSmartFiltersUpgradeModal();
                        return;
                      }
                      const nextEnabled = !smartFilterEnabled;
                      setSmartFilterEnabled(nextEnabled);
                      clearSmartFilterFeedback();
                      if (!nextEnabled) {
                        resetSmartFilterInterpretation('idle');
                        posthog?.capture('website_filter_disabled');
                        return;
                      }
                      if (nextEnabled) setWebsiteStatus('has');
                      setSmartFilterState(smartFilterDescription.trim() ? 'dirty' : 'idle');
                      posthog?.capture('website_filter_enabled');
                    }}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033] ${
                      toggleDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    } ${smartFilterEnabled && !toggleDisabled ? 'bg-[#8A3033]' : 'bg-gray-300'}`}
                    aria-label="Toggle website match"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        smartFilterEnabled && !toggleDisabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {!smartFilterEnabled && smartFilterNotice && (
                  <div className="border-t border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-sm text-amber-900">{smartFilterNotice}</p>
                  </div>
                )}

                {smartFilterEnabled && !toggleDisabled && (
                  <div className="space-y-4 border-t border-[#8A3033]/10 bg-white p-4 sm:p-5">
                    {smartFilterStale && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                        <p className="text-sm font-semibold text-amber-900">
                          Your website match filters were built for "{smartFilterStale.previousBusinessType}."
                        </p>
                        <p className="mt-1 text-sm text-amber-800">
                          Regenerate them for "{searchParams.search_query.trim()}" so matches stay relevant.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            onClick={() => {
                              setSmartFilterStale(null);
                              void handleInterpretFilter();
                            }}
                          >
                            Regenerate filters
                          </Button>
                          <button
                            type="button"
                            onClick={() => {
                              setSmartFilterStale(null);
                              posthog?.capture('website_filter_stale_kept');
                            }}
                            className="text-sm font-semibold text-amber-900 underline underline-offset-2"
                          >
                            Keep as-is
                          </button>
                        </div>
                      </div>
                    )}

                    <div className={smartFilterStale ? 'opacity-60 pointer-events-none select-none' : ''}>
                      <label htmlFor="website-filter-description" className="text-sm font-semibold text-gray-800">
                        What should their website mention?
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        Services, offers, or signals you can see on their site, e.g. "emergency service", "home-service", "insurance claims".
                      </p>
                      <textarea
                        id="website-filter-description"
                        value={smartFilterDescription}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setSmartFilterDescription(nextValue);
                          clearSmartFilterFeedback();
                          resetSmartFilterInterpretation(nextValue.trim() ? 'dirty' : 'idle');
                        }}
                        rows={3}
                        placeholder="e.g., free estimate, emergency service, commercial work"
                        className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        aria-describedby="website-filter-help website-filter-feedback"
                      />
                    </div>

                    {!smartFilterStale && (
                      <>
                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            type="button"
                            onClick={handleInterpretFilter}
                            disabled={smartFilterActionDisabled}
                            title={smartFilterActionHint ?? undefined}
                          >
                            {smartFilterLoading ? 'Generating website match filters…' : smartFilterState === 'approved' ? 'Update filters' : 'Generate filters'}
                          </Button>
                          <button
                            type="button"
                            onClick={() => {
                              setSmartFilterEnabled(false);
                              setSmartFilterStale(null);
                              clearSmartFilterFeedback();
                              resetSmartFilterInterpretation('idle');
                              posthog?.capture('website_filter_disabled');
                            }}
                            className="text-sm font-semibold text-[#8A3033] underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033]"
                          >
                            Turn off
                          </button>
                        </div>

                        <div id="website-filter-help" className="text-xs text-gray-500">
                          {smartFilterActionHint ?? 'We\'ll turn your request into website-ready service terms. You can continue filling in the location while this runs.'}
                        </div>

                        {(smartFilterError || smartFilterNotice || smartFilterLoading) && (
                          <div
                            id="website-filter-feedback"
                            aria-live="polite"
                            className={`rounded-lg border p-3 ${
                              smartFilterError
                                ? 'border-red-200 bg-red-50'
                                : smartFilterLoading
                                  ? 'border-blue-200 bg-blue-50'
                                  : 'border-amber-200 bg-amber-50'
                            }`}
                          >
                            {smartFilterLoading && (
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                                <p className="text-sm text-blue-900">
                                  Turning your request into website-ready service terms. You can continue filling in the location while we work.
                                </p>
                              </div>
                            )}
                            {!smartFilterLoading && smartFilterNotice && (
                              <p className="text-sm text-amber-900">{smartFilterNotice}</p>
                            )}
                            {!smartFilterLoading && smartFilterError && (
                              <>
                                <p className="text-sm text-red-700">{smartFilterError}</p>
                                {smartFilterInterpretation?.status === 'rejected' && smartFilterInterpretation.suggested_rewrite && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSmartFilterDescription(smartFilterInterpretation.suggested_rewrite);
                                      clearSmartFilterFeedback();
                                      resetSmartFilterInterpretation('dirty');
                                      posthog?.capture('website_filter_suggested_rewrite_used');
                                    }}
                                    className="mt-2 text-sm font-semibold text-[#8A3033] underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033]"
                                  >
                                    Use suggested wording
                                  </button>
                                )}
                                {smartFilterState === 'failed' && (
                                  <button
                                    type="button"
                                    onClick={() => { void handleInterpretFilter(); }}
                                    className="mt-2 block text-sm font-semibold text-[#8A3033] underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033]"
                                  >
                                    Try again
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {smartFilterInterpretation && smartFilterState === 'approved' && smartFilterInterpretation.status !== 'rejected' && (
                          <div ref={smartFilterChipsRef} className="rounded-xl border border-blue-200 bg-blue-50/70 p-4">
                            <p className="text-xs font-bold uppercase tracking-wide text-blue-700">We'll prioritize businesses offering</p>
                            {smartFilterInterpretation.offerings.length > 0 && (
                              <div className="mt-3">
                                <div className="flex flex-wrap gap-2">
                                  {smartFilterInterpretation.offerings.map(tag => {
                                    const isNew = smartFilterNewTags.has(tag);
                                    return (
                                      <button
                                        key={tag}
                                        type="button"
                                        onClick={() => removeSmartFilterTag(tag)}
                                        className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium shadow-sm transition hover:shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 active:scale-[0.98] ${
                                          isNew
                                            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 animate-pulse'
                                            : 'border-blue-200 bg-white text-blue-950'
                                        }`}
                                        aria-label={`Remove ${tag}`}
                                      >
                                        {tag}<X className="h-3.5 w-3.5" aria-hidden="true" />
                                      </button>
                                    );
                                  })}
                                </div>
                                {smartFilterNewTags.size > 0 && (
                                  <p className="mt-2 text-[11px] font-semibold text-emerald-700">
                                    ↑ {smartFilterNewTags.size} new term{smartFilterNewTags.size !== 1 ? 's' : ''} added - remove any that don't fit.
                                  </p>
                                )}
                              </div>
                            )}
                            {smartFilterInterpretation.warnings.map(warning => (
                              <p key={warning} className="mt-2 text-xs text-amber-800">{warning}</p>
                            ))}
                            <p className="mt-3 text-xs leading-relaxed text-blue-800">
                              {smartFilterInterpretation.offerings.length > 0
                                ? `We turned your description into ${smartFilterInterpretation.offerings.length} website-ready term${smartFilterInterpretation.offerings.length !== 1 ? 's' : ''}. Website matches appear first; broader ${searchParams.search_query || 'business'} results may follow if needed.`
                                : `We'll check company websites for any of these services. Website matches appear first; broader ${searchParams.search_query || 'business'} results may follow if needed.`}
                            </p>
                          </div>
                        )}

                        {smartFilterState === 'dirty' && smartFilterDescription.trim() && !smartFilterLoading && !smartFilterNotice && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <p className="text-sm text-amber-900">
                              Your service request has changed. Generate updated website match filters before you continue.
                            </p>
                          </div>
                        )}

                        {smartFilterInterpretation?.status === 'rejected' && smartFilterInterpretation.suggested_rewrite && !smartFilterLoading && (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                            We can verify concrete services on company websites, but not quality, budget, or buying intent. Suggested wording: <span className="font-medium text-gray-900">{smartFilterInterpretation.suggested_rewrite}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          </section>
          )}
          
          <div className="order-2 mb-4">
            <div className="mb-3 flex items-center">
              <MapPin className="mr-2 h-5 w-5 shrink-0 text-primary md:h-6 md:w-6" />
              <label className="block text-base font-semibold text-gray-700">
                Location <span className="text-primary">*</span>
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="flex items-center mb-1">
                  <Building2 className="h-4 w-4 text-primary mr-2" />
                  <label htmlFor="city_name" className="block text-gray-700 text-sm">City <span className="text-primary">*</span></label>
                </div>
                <Input
                  id="city_name"
                  name="city_name"
                  type="text"
                  label=""
                  placeholder="City name"
                  value={searchParams.city_name}
                  onChange={handleChange}
                  error={fieldErrors.city_name}
                  required
                />
              </div>
              
              <div>
                <div className="flex items-center mb-1">
                  <Building2 className="h-4 w-4 text-primary mr-2" />
                  <label htmlFor="state_name" className="block text-gray-700 text-sm">State/Province <span className="text-primary">*</span></label>
                </div>
                <Input
                  id="state_name"
                  name="state_name"
                  type="text"
                  label=""
                  placeholder="State / Province"
                  value={searchParams.state_name}
                  onChange={handleChange}
                  error={fieldErrors.state_name}
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <div className="mb-3 flex items-center">
                <Globe className="mr-2 h-5 w-5 shrink-0 text-primary md:h-6 md:w-6" />
                <label htmlFor="country" className="block text-base font-semibold text-gray-700">Country <span className="text-primary">*</span></label>
              </div>
              <CountrySelect
                id="country"
                value={searchParams.country}
                onChange={(countryName) => {
                  if (showOnboardingNotice) setShowOnboardingNotice(false);
                  setSearchParams(prev => ({ ...prev, country: countryName }));
                }}
                placeholder="Select a country..."
                error={fieldErrors.country}
              />
            </div>
          </div>

          <div className="order-5">
            <AutoFindPreview
              planName={currentPlan}
              balance={balance}
              onUpgradeClick={startCheckoutWithDefaultPaidPlan}
              noWebsite={websiteStatus === 'none'}
            />
          </div>

          {SHOW_GOOGLE_MAPS_REQUIREMENTS && (
          <div className="order-6 mb-4 p-4 rounded-lg border-2 border-gray-200 bg-gray-50/50">
            <div className="flex items-center mb-3">
              <div className="bg-primary/10 p-2 rounded-full mr-3">
                <SlidersHorizontal className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-gray-800">Google Maps requirements</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Filter businesses at scrape time. Leave as Any to skip.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min review count</label>
                <Input
                  type="number"
                  min={0}
                  placeholder="Any"
                  value={googleMapsRequirements.minReviewCount}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, minReviewCount: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max review count</label>
                <Input
                  type="number"
                  min={0}
                  placeholder="Any"
                  value={googleMapsRequirements.maxReviewCount}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, maxReviewCount: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min rating</label>
                <Input
                  type="number"
                  min={0}
                  max={5}
                  step={0.1}
                  placeholder="Any"
                  value={googleMapsRequirements.minReviewRating}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, minReviewRating: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max rating</label>
                <Input
                  type="number"
                  min={0}
                  max={5}
                  step={0.1}
                  placeholder="Any"
                  value={googleMapsRequirements.maxReviewRating}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, maxReviewRating: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Has phone</label>
                <select
                  value={googleMapsRequirements.hasPhoneNumber}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, hasPhoneNumber: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Require</option>
                  <option value="false">Exclude</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Has website</label>
                <select
                  value={googleMapsRequirements.hasWebsite}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, hasWebsite: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Require</option>
                  <option value="false">Exclude</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temporary closed</label>
                <select
                  value={googleMapsRequirements.temporaryClosed}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, temporaryClosed: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Only closed</option>
                  <option value="false">Must be open</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LGBTQ+ friendly</label>
                <select
                  value={googleMapsRequirements.lgbtqFriendly}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, lgbtqFriendly: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Require</option>
                  <option value="false">Exclude</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Latino-owned</label>
                <select
                  value={googleMapsRequirements.latinoOwned}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, latinoOwned: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Require</option>
                  <option value="false">Exclude</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asian-owned</label>
                <select
                  value={googleMapsRequirements.asianOwned}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, asianOwned: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Require</option>
                  <option value="false">Exclude</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Veteran-owned</label>
                <select
                  value={googleMapsRequirements.veteranOwned}
                  onChange={(e) => setGoogleMapsRequirements((p) => ({ ...p, veteranOwned: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Require</option>
                  <option value="false">Exclude</option>
                </select>
              </div>
            </div>
          </div>
          )}
          
          <div className="order-7 flex flex-col items-center justify-between gap-3 border-t border-gray-200 pt-5 sm:flex-row">
            <p className="text-xs font-medium text-gray-500">Step 1 of 2 · You will review the search area next</p>
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px] w-full sm:w-auto"
              onClick={() => navigate('/searches')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={geocodingLoading}
              className="min-h-[44px] w-full sm:w-auto"
            >
              {geocodingLoading ? 'Finding location…' : 'Continue to map'}
            </Button>
            </div>
          </div>

          {/* Included in every search */}
          <div className="order-8 mt-1 text-center text-xs leading-relaxed text-gray-500">
            Includes business details, contact channels, Google ratings, decision makers, and verified emails.
          </div>
        </form>
      </Card>
      
      {geocodedCity && (
        <ConfirmSearchModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSearch}
          searchParams={searchParams}
          geocodedCity={geocodedCity}
          isLoading={loading}
          websiteFilter={
            hasApprovedSmartFilter && smartFilterInterpretation
              ? {
                  enabled: true,
                  description: smartFilterDescription,
                  interpretation: smartFilterInterpretation.interpretation,
                  offerings: smartFilterInterpretation.offerings,
                  short_offerings: smartFilterInterpretation.short_offerings,
                  customers: [],
                  warnings: smartFilterInterpretation.warnings,
                }
              : null
          }
          googleMapsRequirements={buildCurrentLeadRequirements()}
        />
      )}
      
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          if (upgradeModalContext === 'smart_filters') {
            posthog?.capture('smart_filters_paywall_dismissed', {
              current_plan: currentPlan,
              business_type: searchParams.search_query.trim() || null,
              source: 'search_create_refine',
            });
          }
          setShowUpgradeModal(false);
          setUpgradeModalContext('generic');
          setSearchLimitInfo(undefined);
          setDiscoverSummary(undefined);
          setDiscoverBestSearch(null);
        }}
        currentPlan={currentPlan}
        lockedPlacesCount={0}
        isLockedPageAccess={false}
        context={upgradeModalContext}
        recommendedPlanName={upgradeModalContext === 'smart_filters' ? 'starter' : undefined}
        plansOnly={upgradeModalContext === 'search_limit' || upgradeModalContext === 'smart_filters'}
        searchLimitInfo={searchLimitInfo}
        discoverSummary={discoverSummary}
        bestSearch={discoverBestSearch}
        title={
          upgradeModalContext === 'search_limit' || upgradeModalContext === 'discover_exhausted' || upgradeModalContext === 'smart_filters'
            ? undefined
            : searchParams.search_query && searchParams.city_name
              ? `Upgrade to search ${searchParams.search_query} in ${searchParams.city_name}`
              : 'Choose a plan to continue'
        }
      />

      {redirectingToCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95">
          <div className="text-center">
            <div className="inline-block animate-bounce text-6xl mb-4">
              🚀
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Redirecting to checkout...
            </h3>
            <p className="text-gray-600">
              Please wait while we prepare your payment
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchForm; 
